import { useEffect, useRef } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../shared/dropdown";
import { SharedIcon } from "../shared/shared-icon";
import { useCollectionCardMenu } from "../../collection-card-menu";
import { useSaveCollectionColoursModalStore } from "../../../js/stores/save-collection-colours-modal-store";
import { useSaveAsNewCollectionModalStore } from "../../../js/stores/save-as-new-collection-modal-store";
import { usePendingCollectionSaveStore } from "../../../js/stores/pending-collection-save-store";

/**
 * The "…" menu on a colour collection card, and on the collection's own page.
 *
 * Built on the shared Radix dropdown, which is what satisfies the keyboard requirement: Enter and
 * Space open it, arrows move through it, Escape closes it and focus returns to the trigger. None of
 * that is hand-rolled here, and it is the reason this is not the existing
 * `js/modules/edit-collection-menu.js` — that one is bound to a single hard-coded element id, so it
 * cannot be used once per card, and it manages focus not at all.
 */
export function CardActionsMenu() {
  const {
    collectionId,
    isLoggedIn,
    loginFormId,
    saveColoursFormUrl,
    saveAsNewCollectionFormUrl,
    menuLabel,
    saveColoursLabel,
    saveAsNewCollectionLabel,
  } = useCollectionCardMenu();

  /**
   * Whether this menu was opened from the keyboard.
   *
   * A ref rather than state on purpose: it must not cause a render, and it is read inside a Radix
   * event handler rather than during rendering, so a stale render is not a concern.
   */
  const openedByKeyboard = useRef(false);

  const openColoursModal = useSaveCollectionColoursModalStore((state) => state.openModal);
  const openAsNewModal = useSaveAsNewCollectionModalStore((state) => state.openModal);

  const pending = usePendingCollectionSaveStore((state) => state.pending);
  const setPending = usePendingCollectionSaveStore((state) => state.setPending);
  const clearPending = usePendingCollectionSaveStore((state) => state.clearPending);
  const hasHydrated = usePendingCollectionSaveStore((state) => state._hasHydrated);

  const openSave = (action) => {
    if (action === "collection") {
      openAsNewModal({ formUrl: saveAsNewCollectionFormUrl, collectionId });
      return;
    }

    openColoursModal({ formUrl: saveColoursFormUrl, collectionId });
  };

  /**
   * Picks the save back up after signing in.
   *
   * It reopens the modal rather than posting the save outright, and that is deliberate. Writing to
   * someone's account unprompted as a page finishes loading after an OAuth redirect is a surprising
   * thing to do to a person; and the collection copy needs a name and a room type that were never
   * collected before they left. The member lands back where they were, one click from done.
   *
   * The pending entry is cleared as the modal opens, not after the save succeeds — otherwise a
   * member who changes their mind and closes the modal reopens it on every subsequent page load.
   */
  useEffect(() => {
    if (!isLoggedIn || !hasHydrated || !pending) return;
    if (pending.collectionId !== collectionId) return;

    clearPending();
    openSave(pending.action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, hasHydrated, pending, collectionId]);

  const handleSelect = (action) => {
    if (isLoggedIn) {
      openSave(action);
      return;
    }

    // Remember what they asked for, then hand over to the header's own sign-in form. Reusing that
    // form rather than linking somewhere is what carries the identity provider and the returnUrl
    // the real auth flow needs; a hand-built link would drop both.
    setPending({ action, collectionId });

    document.getElementById(loginFormId)?.submit();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="collection-card-menu__trigger"
        aria-label={menuLabel}
        type="button"
        // Which input opened the menu. Read below to decide where focus should end up when it
        // closes, because by then the browser can no longer tell us: Radix moves focus into the
        // menu and back out programmatically, and after that `:focus-visible` reports true for a
        // mouse user as readily as a keyboard one. Recording the modality ourselves at the one
        // moment it is unambiguous is the only reliable answer.
        onPointerDown={() => {
          openedByKeyboard.current = false;
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " " || event.key.startsWith("Arrow")) {
            openedByKeyboard.current = true;
          }
        }}
        onClick={(event) => {
          // The card is one big link. Without this the browser follows it the moment the trigger
          // inside it is clicked and the menu never appears.
          event.stopPropagation();
        }}
      >
        <SharedIcon iconName="#sprite-ellipsis" className="collection-card-menu__icon" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="collection-card-menu__content"
        align="end"
        // Somebody who opened the menu with the mouse but is now driving it with the keyboard -
        // arrows, Escape - has become a keyboard user, and should get focus back on the trigger
        // like any other. Without this they would be dropped onto the body with nothing focused.
        onKeyDown={() => {
          openedByKeyboard.current = true;
        }}
        onCloseAutoFocus={(event) => {
          // THE FIX FOR THE STICKY TRIGGER. Radix's default is to hand focus back to the trigger
          // when the menu closes, which is right for a keyboard user and wrong for a mouse one:
          // it left the icon lit on a card the pointer had long since left, because the card was
          // still :focus-within / :focus-visible.
          //
          // For a pointer user, suppress the focus return entirely. Nothing is focused, so the
          // icon fades out with the hover, which is what the design asks for. For a keyboard user
          // the default stands, so focus is never stranded and they keep their place on the card.
          if (!openedByKeyboard.current) {
            event.preventDefault();
          }
        }}
      >
        <DropdownMenuItem
          className="collection-card-menu__item"
          onSelect={() => handleSelect("collection")}
        >
          {saveAsNewCollectionLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="collection-card-menu__item"
          onSelect={() => handleSelect("colours")}
        >
          {saveColoursLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
